import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query'
import {
  type AddContractAnalysisType,
  CreateContract,
  RejectContractAnalysisType,
  UpdateContract,
  UpdateContractAnalysisType,
  useAuth
} from '../index'
import { throwOnError } from '../utils/throwOnError'

export const useGetContractsQuery = (isEnabled = true) => {
  const { authFetch } = useAuth()
  return useQuery({
    enabled: isEnabled,
    queryKey: ['contracts'],
    queryFn: async () => {
      const { data, error } = await authFetch().GET('/contracts')
      if (error) {
        throwOnError(error)
      }
      return data
    }
  })
}

export const useGetContractByIdQuery = (contractId?: number) => {
  const { authFetch } = useAuth()
  return useQuery({
    queryKey: ['contracts', contractId],
    queryFn: contractId
      ? async () => {
          const { data, error } = await authFetch().GET(
            '/contracts/{contractId}',
            {
              params: {
                path: {
                  contractId
                }
              }
            }
          )
          if (error) {
            throwOnError(error)
          }
          return data
        }
      : skipToken
  })
}

export const useCreateContractMutation = () => {
  const queryClient = useQueryClient()
  const { authFetch } = useAuth()
  return useMutation({
    mutationFn: async (contract: CreateContract) => {
      const { data, error } = await authFetch().POST('/contracts', {
        body: contract
      })
      if (error) {
        throwOnError(error)
      }
      return data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['contracts']
      })
    }
  })
}

interface UpdateContractMutationVariables {
  contractId: number
  etag: string
  body: UpdateContract
}

export const useUpdateContractMutation = () => {
  const queryClient = useQueryClient()
  const { authFetch } = useAuth()
  return useMutation({
    mutationFn: async ({
      contractId,
      etag,
      body
    }: UpdateContractMutationVariables) => {
      const { data, error } = await authFetch().PUT('/contracts/{contractId}', {
        params: {
          header: {
            'If-Match': etag
          },
          path: { contractId }
        },
        body
      })
      if (error) {
        throwOnError(error)
      }
      return data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['contracts']
      })
    }
  })
}

interface DeleteContractMutationVariables {
  contractId: number
  etag: string
}

export const useDeleteContractMutation = () => {
  const queryClient = useQueryClient()
  const { authFetch } = useAuth()
  return useMutation({
    mutationFn: async ({ contractId, etag }: DeleteContractMutationVariables) =>
      await authFetch().DELETE('/contracts/{contractId}', {
        params: {
          header: {
            'If-Match': etag
          },
          path: { contractId }
        }
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['contracts']
      })
    }
  })
}

interface AddContractAnalysisTypeMutationVariables {
  contractId: number
  body: AddContractAnalysisType
}

export const useAddContractAnalysisTypeMutation = () => {
  const queryClient = useQueryClient()
  const { authFetch } = useAuth()
  return useMutation({
    mutationFn: async ({
      contractId,
      body
    }: AddContractAnalysisTypeMutationVariables) => {
      const { data, error } = await authFetch().POST(
        '/contracts/{contractId}/analysis-types',
        {
          params: {
            path: {
              contractId
            }
          },
          body
        }
      )
      if (error) {
        throwOnError(error)
      }
      return data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['contracts']
      })
    }
  })
}

interface DeleteContractAnalysisTypeMutationVariables {
  contractId: number
  analysisTypeId: number
  etag: string
}

export const useDeleteContractAnalysisTypeMutation = () => {
  const queryClient = useQueryClient()
  const { authFetch } = useAuth()
  return useMutation({
    mutationFn: async ({
      contractId,
      analysisTypeId,
      etag
    }: DeleteContractAnalysisTypeMutationVariables) =>
      await authFetch().DELETE('/contracts/{contractId}/analysis-types/{id}', {
        params: {
          header: {
            'If-Match': etag
          },
          path: {
            contractId,
            id: analysisTypeId
          }
        }
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['contracts']
      })
    }
  })
}

interface UpdateAnalysisTypeMutationVariables {
  contractId: number
  analysisTypeId: number
  body: UpdateContractAnalysisType
}

export const useUpdateAnalysisTypeMutation = () => {
  const { authFetch } = useAuth()
  return useMutation({
    mutationFn: async ({
      contractId,
      analysisTypeId,
      body
    }: UpdateAnalysisTypeMutationVariables) => {
      const { data, error } = await authFetch().PUT(
        '/contracts/{contractId}/analysis-types/{id}',
        {
          params: {
            path: {
              contractId,
              id: analysisTypeId
            }
          },
          body
        }
      )
      if (error) {
        throwOnError(error)
      }
      return data
    }
  })
}

interface ApproveAnalysisTypeMutationVariables {
  contractId: number
  analysisTypeId: number
}

export const useApproveAnalysisTypeMutation = () => {
  const { authFetch } = useAuth()
  return useMutation({
    mutationFn: async ({
      contractId,
      analysisTypeId
    }: ApproveAnalysisTypeMutationVariables) => {
      const { data, error } = await authFetch().POST(
        '/contracts/{contractId}/analysis-types/{id}/approve',
        {
          params: {
            path: {
              contractId,
              id: analysisTypeId
            }
          }
        }
      )
      if (error) {
        throwOnError(error)
      }
      return data
    }
  })
}

interface RejectAnalysisTypeMutationVariables {
  contractId: number
  analysisTypeId: number
  body: RejectContractAnalysisType
}

export const useRejectAnalysisTypeMutation = () => {
  const { authFetch } = useAuth()
  return useMutation({
    mutationFn: async ({
      contractId,
      analysisTypeId,
      body
    }: RejectAnalysisTypeMutationVariables) => {
      const { data, error } = await authFetch().POST(
        '/contracts/{contractId}/analysis-types/{id}/reject',
        {
          params: {
            path: {
              contractId,
              id: analysisTypeId
            }
          },
          body
        }
      )
      if (error) {
        throwOnError(error)
      }
      return data
    }
  })
}
